import { createSelector } from '@ngrx/store';

import * as durationHelpers from '../../helpers/duration.helpers';
import * as orderHelpers from '../../helpers/order.helpers';
import { Order, OrderTypes } from '../../models/order.model';
import { selectDashboardState } from '../dashboard.selectors';
import { selectProductsState } from 'app/store/products/products.selectors';
import { SubscriptionPlan } from 'app/models/subscrition-plan.model';

const selectOrderState = createSelector(
  selectDashboardState,
  (dashboardState) => dashboardState.order
);

export const selectOrder = createSelector(
  selectOrderState,
  (orderState) => orderState.order
);

export const selectDoesOrderHaveFamilyOption = createSelector(
  selectOrder,
  (order) => order.hasFamilyOption
);

export const selectOrderType = createSelector(selectOrder, (order) => {
  return orderHelpers.getOrderType(order);
});

export const selectOrderPlan = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productsState) => {
    return orderHelpers.getOrderPlan(
      dashboardState.order.order,
      productsState.subscriptionsState.subscriptionsProducts
    );
  }
);

export const selectOrderVoucherPlan = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    return orderHelpers.getOrderVoucherPlan(
      dashboardState.order.order,
      productState.giftsCardsState.giftsCardsProducts
    );
  }
);

export const selectIsOrderAMigration = createSelector(
  selectDashboardState,
  (dashboardState) => {
    return orderHelpers.getIsMigration(
      dashboardState.order.order,
      dashboardState.subscriptions.activeSubscription
    );
  }
);

export const selectOrderPrice = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    const order: Order | null = dashboardState.order.order;

    return orderHelpers.getOrderPrice({
      order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
      familyOptionPrice: productState.optionsState.optionsProducts.find(
        (sub: SubscriptionPlan) => sub.name === 'family'
      ).price,
      plans: productState.subscriptionsState.subscriptionsProducts,
      voucherPlans: productState.giftsCardsState.giftsCardsProducts,
    });
  }
);

export const selectPriceToPayNow = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    const order: Order | null = dashboardState.order.order;

    return orderHelpers.getPriceToPayNow({
      order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
      familyOptionPrice: productState.optionsState.optionsProducts.find(
        (sub: SubscriptionPlan) => sub.name === 'family'
      ).price,
      plans: productState.subscriptionsState.subscriptionsProducts,
      voucherPlans: productState.giftsCardsState.giftsCardsProducts,
    });
  }
);

export const selectPriceToPayNowAfterPromote = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    const order: Order | null = dashboardState.order.order;

    const beforePromote: number = orderHelpers.getPriceToPayNow({
      order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
      familyOptionPrice: productState.optionsState.optionsProducts.find(
        (sub: SubscriptionPlan) => sub.name === 'family'
      ).price,
      plans: productState.subscriptionsState.subscriptionsProducts,
      voucherPlans: productState.giftsCardsState.giftsCardsProducts,
    });

    if (!order || !order.coupon) {
      return beforePromote;
    }

    if (order.coupon.amount_off) {
      return beforePromote - order.coupon.amount_off < 0
        ? 0
        : beforePromote - order.coupon.amount_off;
    } else if (order.coupon.percent_off) {
      return beforePromote - (beforePromote * order.coupon.percent_off) / 100;
    }

    return beforePromote;
  }
);

export const selectFuturePrice = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    const order: Order | null = dashboardState.order.order;

    return orderHelpers.getFuturePrice({
      order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
      familyOptionPrice: productState.optionsState.optionsProducts.find(
        (sub: SubscriptionPlan) => sub.name === 'family'
      ).price,
      plans: productState.subscriptionsState.subscriptionsProducts,
      voucherPlans: productState.giftsCardsState.giftsCardsProducts,
      coupon: dashboardState.order.order.coupon,
    });
  }
);

export const selectFuturePriceDate = createSelector(
  selectDashboardState,
  (dashboardState) => {
    return orderHelpers.getFuturePriceDate({
      order: dashboardState.order.order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
    });
  }
);

export const selectOrderFamilyOptionDate = createSelector(
  selectDashboardState,
  (dashboardState) => {
    return orderHelpers.getOrderFamilyOptionDate({
      order: dashboardState.order.order,
      activeSubscription: dashboardState.subscriptions.activeSubscription,
    });
  }
);

export const selectDisplayedVoucherDuration = createSelector(
  selectDashboardState,
  (dashboardState) => {
    if (
      orderHelpers.getOrderType(dashboardState.order.order) !==
      OrderTypes.Voucher
    ) {
      return null;
    }
    return durationHelpers.defineDisplayedVoucherDuration(
      dashboardState.order.order.voucherDuration
    );
  }
);

export const selectIsLoadingPaymentOrCancellation = createSelector(
  selectOrderState,
  (orderState) => orderState.isLoadingPaymentOrCancellation
);

export const selectIsOrderAFamilyOptionCancellation = createSelector(
  selectOrder,
  (order) => {
    return orderHelpers.getIsOrderAFamilyOptionCancellation(order);
  }
);

export const selectOrderCoupon = createSelector(selectOrder, (order) => {
  return order.coupon;
});

export const selectNextPeriodPrice = createSelector(
  selectDashboardState,
  selectProductsState,
  (dashboardState, productState) => {
    const order: Order | null = dashboardState.order.order;

    return orderHelpers.getNextPeriodPrice({
      order,
      plans: productState.subscriptionsState.subscriptionsProducts,
    });
  }
);
